import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Minus, Plus, X, Check } from "lucide-react";
import { SAUCES, BOISSONS, CRUDITES, SUPPORTS } from "../mock/menuData";
import { useCart } from "../context/CartContext";
import { toast } from "../hooks/use-toast";

const fmt = (n) => n.toFixed(2).replace(".", ",") + "€";

const ItemCustomizeModal = ({ item, open, onClose }) => {
  const { addItem } = useCart();
  const formats = useMemo(() => {
    const f = [];
    if (item?.seul != null) f.push("seul");
    if (item?.menu != null) f.push("menu");
    return f;
  }, [item]);

  const [format, setFormat] = useState("seul");
  const [meats, setMeats] = useState([]);
  const [sauces, setSauces] = useState([]);
  const [boisson, setBoisson] = useState(BOISSONS[0]);
  const [crudites, setCrudites] = useState([...CRUDITES]);
  const [support, setSupport] = useState(SUPPORTS[0]);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    if (item) {
      setFormat(formats[0] || "seul");
      setMeats([]);
      setSauces([]);
      setBoisson(BOISSONS[0]);
      setCrudites([...CRUDITES]);
      setSupport(SUPPORTS[0]);
      setQty(1);
    }
  }, [item, formats]);

  if (!item) return null;

  const unitPrice = format === "menu" ? item.menu : item.seul;
  const needMeat = item.meat || 0;
  const showBoisson = format === "menu";

  const addMeat = (m) => {
    if (meats.length >= needMeat) return;
    setMeats((prev) => [...prev, m]);
  };
  const removeMeat = (idx) => setMeats((prev) => prev.filter((_, i) => i !== idx));

  const toggleSauce = (s) => {
    setSauces((prev) => {
      if (prev.includes(s)) return prev.filter((x) => x !== s);
      if (prev.length >= 2) return prev;
      return [...prev, s];
    });
  };

  const canAdd = needMeat ? meats.length === needMeat : true;

  const toggleCrudite = (c) => {
    setCrudites((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  };

  const handleAdd = () => {
    if (!canAdd) {
      toast({ title: "Choix incomplet", description: `Sélectionnez ${needMeat} viande(s).` });
      return;
    }
    addItem({
      id: item.id,
      name: item.name,
      image: item.image,
      format,
      meats,
      sauces,
      crudites: item.crudites ? crudites : [],
      support: item.support ? support : null,
      boisson: showBoisson ? boisson : null,
      unitPrice,
      qty,
    });
    toast({ title: "Ajouté au panier 🛒", description: `${qty}× ${item.name} (${format})` });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#161616] border border-white/10 text-white max-w-lg p-0 overflow-hidden max-h-[90vh] flex flex-col">
        <div className="relative h-40 shrink-0">
          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#161616] to-transparent" />
          <DialogHeader className="absolute bottom-3 left-4 right-4">
            <DialogTitle className="font-display text-2xl uppercase text-left">{item.name}</DialogTitle>
            <DialogDescription className="sr-only">Personnalisez {item.name} : formule, viandes, sauces et boisson.</DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-5 overflow-y-auto space-y-5">
          <p className="text-white/60 text-sm">{item.desc}</p>

          {formats.length > 1 && (
            <div>
              <p className="font-display uppercase text-sm text-gold mb-2">Formule</p>
              <div className="grid grid-cols-2 gap-3">
                {formats.map((f) => (
                  <button
                    key={f}
                    onClick={() => setFormat(f)}
                    className={`rounded-xl border-2 px-3 py-2.5 text-left transition-all ${
                      format === f ? "border-pink bg-pink/10" : "border-white/15 hover:border-white/40"
                    }`}
                  >
                    <span className="block font-semibold capitalize">{f}</span>
                    <span className="block text-xs text-white/50">{f === "menu" ? "+ frites + boisson" : "seul"}</span>
                    <span className="block text-gold font-bold mt-1">{fmt(f === "menu" ? item.menu : item.seul)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {needMeat > 0 && (
            <div>
              <p className="font-display uppercase text-sm text-gold mb-2">
                Viandes <span className="text-white/40 normal-case font-body">({meats.length}/{needMeat})</span>
              </p>
              {meats.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {meats.map((m, i) => (
                    <span key={`${m}-${i}`} className="flex items-center gap-1 bg-pink text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                      {m} <button onClick={() => removeMeat(i)}><X size={12} /></button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {item.meatList.map((m) => (
                  <button
                    key={m}
                    onClick={() => addMeat(m)}
                    disabled={meats.length >= needMeat}
                    className="text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20 hover:border-pink hover:text-pink transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    + {m}
                  </button>
                ))}
              </div>
            </div>
          )}

          {item.support && (
            <div>
              <p className="font-display uppercase text-sm text-gold mb-2">
                Pain <span className="text-white/40 normal-case font-body">(offert)</span>
              </p>
              <div className="grid grid-cols-2 gap-3">
                {SUPPORTS.map((s) => (
                  <button
                    key={s}
                    data-testid={`support-${s.replace(/\s+/g, "-").toLowerCase()}-btn`}
                    onClick={() => setSupport(s)}
                    className={`rounded-xl border-2 px-3 py-2.5 text-center font-semibold transition-all flex items-center justify-center gap-1 ${
                      support === s ? "border-pink bg-pink/10 text-pink" : "border-white/15 hover:border-white/40"
                    }`}
                  >
                    {support === s && <Check size={14} />} {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {item.crudites && (
            <div>
              <p className="font-display uppercase text-sm text-gold mb-2">
                Crudités <span className="text-white/40 normal-case font-body">(offert)</span>
              </p>
              <div className="flex flex-wrap gap-2">
                {CRUDITES.map((c) => (
                  <button
                    key={c}
                    onClick={() => toggleCrudite(c)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1 ${
                      crudites.includes(c) ? "border-pink bg-pink/15 text-pink" : "border-white/20 hover:border-white/50"
                    }`}
                  >
                    {crudites.includes(c) && <Check size={12} />} {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {item.sauce !== false && (
            <div>
              <p className="font-display uppercase text-sm text-gold mb-2">
                Sauces <span className="text-white/40 normal-case font-body">(max 2)</span>
              </p>
              <div className="flex flex-wrap gap-2">
                {SAUCES.map((s) => (
                  <button
                    key={s}
                    onClick={() => toggleSauce(s)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1 ${
                      sauces.includes(s) ? "border-pink bg-pink/15 text-pink" : "border-white/20 hover:border-white/50"
                    }`}
                  >
                    {sauces.includes(s) && <Check size={12} />} {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {showBoisson && (
            <div>
              <p className="font-display uppercase text-sm text-gold mb-2">Boisson</p>
              <div className="flex flex-wrap gap-2">
                {BOISSONS.map((b) => (
                  <button
                    key={b}
                    onClick={() => setBoisson(b)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                      boisson === b ? "border-pink bg-pink/15 text-pink" : "border-white/20 hover:border-white/50"
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-white/10 flex items-center gap-3 shrink-0 bg-[#111]">
          <div className="flex items-center gap-3 bg-white/10 rounded-full px-2 py-1">
            <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-1.5 hover:text-pink"><Minus size={16} /></button>
            <span className="font-bold w-5 text-center">{qty}</span>
            <button onClick={() => setQty((q) => q + 1)} className="p-1.5 hover:text-pink"><Plus size={16} /></button>
          </div>
          <button
            onClick={handleAdd}
            className="flex-1 bg-pink text-white font-display uppercase text-lg py-2.5 rounded-full btn-shine flex items-center justify-center gap-2"
          >
            Ajouter • {fmt(unitPrice * qty)}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ItemCustomizeModal;
